import { useState, FormEvent, ChangeEvent } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

const EDGE_FUNCTION_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-deal-notification`;

const DealSubmissionForm = () => {
  const [formData, setFormData] = useState({
    brokerName: '',
    brokerCompany: '',
    brokerEmail: '',
    brokerPhone: '',
    clientName: '',
    businessName: '',
    businessEmail: '',
    businessPhone: '',
    businessAddress: '',
    city: '',
    state: '',
    zipCode: '',
    entityType: '',
    yearsInBusiness: '',
    hasBusinessCredit: '',
    reportingBureaus: [] as string[],
    serviceRequested: '',
    additionalNotes: '',
    agreement1: false,
    agreement2: false,
    agreement3: false,
  });

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;

      if (name === 'reportingBureaus') {
        const bureauValue = (e.target as HTMLInputElement).value;
        setFormData((prev) => ({
          ...prev,
          reportingBureaus: checked
            ? [...prev.reportingBureaus, bureauValue]
            : prev.reportingBureaus.filter((b) => b !== bureauValue),
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: checked,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedFiles(files);
      setFileNames(files.map((f) => f.name));
    }
  };

  const uploadFilesToStorage = async (): Promise<string[]> => {
    if (selectedFiles.length === 0) return [];

    const uploadedUrls: string[] = [];
    const timestamp = Date.now();

    for (const file of selectedFiles) {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const filePath = `submissions/${timestamp}_${safeName}`;

      const { error } = await supabase.storage
        .from('deal-documents')
        .upload(filePath, file, { upsert: false });

      if (error) {
        console.error('File upload error:', error.message);
        continue;
      }

      const { data: signedData, error: signedError } = await supabase.storage
        .from('deal-documents')
        .createSignedUrl(filePath, 60 * 60 * 24 * 7); // 7-day link

      if (!signedError && signedData?.signedUrl) {
        uploadedUrls.push(signedData.signedUrl);
      }
    }

    return uploadedUrls;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (
      !formData.brokerName || !formData.brokerCompany || !formData.brokerEmail ||
      !formData.brokerPhone || !formData.clientName || !formData.businessName ||
      !formData.businessEmail || !formData.businessPhone || !formData.businessAddress ||
      !formData.city || !formData.state || !formData.zipCode || !formData.entityType ||
      !formData.yearsInBusiness || !formData.hasBusinessCredit || !formData.serviceRequested
    ) {
      setErrorMessage('Please fill in all required fields.');
      setSubmitStatus('error');
      return;
    }

    if (!formData.agreement1 || !formData.agreement2 || !formData.agreement3) {
      setErrorMessage('Please confirm all agreement checkboxes.');
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      // Step 1: Upload files to Supabase Storage
      const fileUrls = await uploadFilesToStorage();

      // Step 2: Submit form data to Readdy form endpoint
      const params = new URLSearchParams();
      params.set('Broker Name', formData.brokerName);
      params.set('Broker Company', formData.brokerCompany);
      params.set('email', formData.brokerEmail);
      params.set('Broker Phone', formData.brokerPhone);
      params.set('Client Full Name', formData.clientName);
      params.set('Business Name', formData.businessName);
      params.set('Business Email', formData.businessEmail);
      params.set('Business Phone', formData.businessPhone);
      params.set('Business Address', formData.businessAddress);
      params.set('City', formData.city);
      params.set('State', formData.state);
      params.set('Zip Code', formData.zipCode);
      params.set('Entity Type', formData.entityType);
      params.set('Years in Business', formData.yearsInBusiness);
      params.set('Business Credit Reporting Status', formData.hasBusinessCredit);
      params.set('Reporting Bureaus', formData.reportingBureaus.length > 0 ? formData.reportingBureaus.join(', ') : 'None selected');
      params.set('Requested Service', formData.serviceRequested);
      params.set('Additional Notes', formData.additionalNotes || 'None');
      params.set('Uploaded Documents', fileUrls.length > 0 ? fileUrls.join(' | ') : (fileNames.length > 0 ? fileNames.join(', ') : 'No documents uploaded'));
      params.set('Confirmation 1 – Client Agreement', formData.agreement1 ? 'Confirmed' : 'Not confirmed');
      params.set('Confirmation 2 – Accurate Information', formData.agreement2 ? 'Confirmed' : 'Not confirmed');
      params.set('Confirmation 3 – Payment Understanding', formData.agreement3 ? 'Confirmed' : 'Not confirmed');

      const formResponse = await fetch('https://readdy.ai/api/form/d6p37uuancq2eie8274g', {
        method: 'POST',
        body: params,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      // Step 3: Send email notification via Edge Function
      await fetch(EDGE_FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ formData, fileUrls }),
      });

      if (formResponse.ok) {
        setSubmitStatus('success');
        setFormData({
          brokerName: '',
          brokerCompany: '',
          brokerEmail: '',
          brokerPhone: '',
          clientName: '',
          businessName: '',
          businessEmail: '',
          businessPhone: '',
          businessAddress: '',
          city: '',
          state: '',
          zipCode: '',
          entityType: '',
          yearsInBusiness: '',
          hasBusinessCredit: '',
          reportingBureaus: [],
          serviceRequested: '',
          additionalNotes: '',
          agreement1: false,
          agreement2: false,
          agreement3: false,
        });
        setSelectedFiles([]);
        setFileNames([]);
      } else {
        setSubmitStatus('error');
        setErrorMessage('Submission failed. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto px-6 py-16">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl shadow-lg p-8 md:p-12"
        data-readdy-form
        id="deal-submission-form"
      >
        {/* Broker Information */}
        <div className="mb-10">
          <h3 className="text-xl font-semibold text-[#2F6FE4] mb-6 pb-3 border-b-2 border-gray-100">
            Broker Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="brokerName" className="block text-sm font-medium text-gray-700 mb-2">
                Broker Name <span className="text-red-500">*</span>
              </label>
              <input type="text" id="brokerName" name="brokerName" value={formData.brokerName} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F6FE4] focus:border-transparent outline-none transition-all text-sm" placeholder="Enter your name" />
            </div>
            <div>
              <label htmlFor="brokerCompany" className="block text-sm font-medium text-gray-700 mb-2">
                Broker Company <span className="text-red-500">*</span>
              </label>
              <input type="text" id="brokerCompany" name="brokerCompany" value={formData.brokerCompany} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F6FE4] focus:border-transparent outline-none transition-all text-sm" placeholder="Enter your company name" />
            </div>
            <div>
              <label htmlFor="brokerEmail" className="block text-sm font-medium text-gray-700 mb-2">
                Broker Email <span className="text-red-500">*</span>
              </label>
              <input type="email" id="brokerEmail" name="brokerEmail" value={formData.brokerEmail} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F6FE4] focus:border-transparent outline-none transition-all text-sm" placeholder="your@email.com" />
            </div>
            <div>
              <label htmlFor="brokerPhone" className="block text-sm font-medium text-gray-700 mb-2">
                Broker Phone <span className="text-red-500">*</span>
              </label>
              <input type="tel" id="brokerPhone" name="brokerPhone" value={formData.brokerPhone} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F6FE4] focus:border-transparent outline-none transition-all text-sm" placeholder="(555) 123-4567" />
            </div>
          </div>
        </div>

        {/* Client Information */}
        <div className="mb-10">
          <h3 className="text-xl font-semibold text-[#2F6FE4] mb-6 pb-3 border-b-2 border-gray-100">
            Client Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="clientName" className="block text-sm font-medium text-gray-700 mb-2">
                Client Full Name <span className="text-red-500">*</span>
              </label>
              <input type="text" id="clientName" name="clientName" value={formData.clientName} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F6FE4] focus:border-transparent outline-none transition-all text-sm" placeholder="Enter client full name" />
            </div>
            <div>
              <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-2">
                Business Name <span className="text-red-500">*</span>
              </label>
              <input type="text" id="businessName" name="businessName" value={formData.businessName} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F6FE4] focus:border-transparent outline-none transition-all text-sm" placeholder="Enter business name" />
            </div>
            <div>
              <label htmlFor="businessEmail" className="block text-sm font-medium text-gray-700 mb-2">
                Business Email <span className="text-red-500">*</span>
              </label>
              <input type="email" id="businessEmail" name="businessEmail" value={formData.businessEmail} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F6FE4] focus:border-transparent outline-none transition-all text-sm" placeholder="business@email.com" />
            </div>
            <div>
              <label htmlFor="businessPhone" className="block text-sm font-medium text-gray-700 mb-2">
                Business Phone <span className="text-red-500">*</span>
              </label>
              <input type="tel" id="businessPhone" name="businessPhone" value={formData.businessPhone} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F6FE4] focus:border-transparent outline-none transition-all text-sm" placeholder="(555) 123-4567" />
            </div>
          </div>
        </div>

        {/* Business Information */}
        <div className="mb-10">
          <h3 className="text-xl font-semibold text-[#2F6FE4] mb-6 pb-3 border-b-2 border-gray-100">
            Business Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label htmlFor="businessAddress" className="block text-sm font-medium text-gray-700 mb-2">
                Business Address <span className="text-red-500">*</span>
              </label>
              <input type="text" id="businessAddress" name="businessAddress" value={formData.businessAddress} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F6FE4] focus:border-transparent outline-none transition-all text-sm" placeholder="Enter business street address" />
            </div>
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">City <span className="text-red-500">*</span></label>
              <input type="text" id="city" name="city" value={formData.city} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F6FE4] focus:border-transparent outline-none transition-all text-sm" placeholder="Enter city" />
            </div>
            <div>
              <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">State <span className="text-red-500">*</span></label>
              <input type="text" id="state" name="state" value={formData.state} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F6FE4] focus:border-transparent outline-none transition-all text-sm" placeholder="Enter state" />
            </div>
            <div>
              <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-2">Zip Code <span className="text-red-500">*</span></label>
              <input type="text" id="zipCode" name="zipCode" value={formData.zipCode} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F6FE4] focus:border-transparent outline-none transition-all text-sm" placeholder="Enter zip code" />
            </div>
            <div>
              <label htmlFor="entityType" className="block text-sm font-medium text-gray-700 mb-2">Entity Type <span className="text-red-500">*</span></label>
              <select id="entityType" name="entityType" value={formData.entityType} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F6FE4] focus:border-transparent outline-none transition-all text-sm cursor-pointer">
                <option value="">Select entity type</option>
                <option value="LLC">LLC</option>
                <option value="Corporation">Corporation</option>
                <option value="Sole Proprietor">Sole Proprietor</option>
                <option value="Partnership">Partnership</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label htmlFor="yearsInBusiness" className="block text-sm font-medium text-gray-700 mb-2">Years in Business <span className="text-red-500">*</span></label>
              <select id="yearsInBusiness" name="yearsInBusiness" value={formData.yearsInBusiness} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F6FE4] focus:border-transparent outline-none transition-all text-sm cursor-pointer">
                <option value="">Select years in business</option>
                <option value="Less than 1 year">Less than 1 year</option>
                <option value="1 to 2 years">1 to 2 years</option>
                <option value="2 to 5 years">2 to 5 years</option>
                <option value="5+ years">5+ years</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="hasBusinessCredit" className="block text-sm font-medium text-gray-700 mb-2">
                Does the business currently have business credit reporting? <span className="text-red-500">*</span>
              </label>
              <select id="hasBusinessCredit" name="hasBusinessCredit" value={formData.hasBusinessCredit} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F6FE4] focus:border-transparent outline-none transition-all text-sm cursor-pointer">
                <option value="">Select an option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Not Sure">Not Sure</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Current Reporting Bureaus <span className="text-red-500">*</span>
              </label>
              <div className="space-y-3">
                {['Dun and Bradstreet', 'Experian Business', 'Equifax Business', 'Not Sure'].map((bureau) => (
                  <label key={bureau} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="reportingBureaus"
                      value={bureau}
                      checked={formData.reportingBureaus.includes(bureau)}
                      onChange={handleInputChange}
                      required={formData.reportingBureaus.length === 0}
                      className="w-5 h-5 text-[#2F6FE4] border-gray-300 rounded focus:ring-2 focus:ring-[#2F6FE4] cursor-pointer"
                    />
                    <span className="ml-3 text-sm text-gray-700">{bureau}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Requested Service */}
        <div className="mb-10">
          <h3 className="text-xl font-semibold text-[#2F6FE4] mb-6 pb-3 border-b-2 border-gray-100">
            Requested Service
          </h3>
          <div>
            <label htmlFor="serviceRequested" className="block text-sm font-medium text-gray-700 mb-2">
              Select Service <span className="text-red-500">*</span>
            </label>
            <select id="serviceRequested" name="serviceRequested" value={formData.serviceRequested} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F6FE4] focus:border-transparent outline-none transition-all text-sm cursor-pointer">
              <option value="">Select a service</option>
              <option value="50K Installment Trade Line">50K Installment Trade Line</option>
              <option value="100K Installment Trade Line">100K Installment Trade Line</option>
              <option value="150K Installment Trade Line">150K Installment Trade Line</option>
              <option value="250K Installment Trade Line">250K Installment Trade Line</option>
              <option value="500K Installment Trade Line">500K Installment Trade Line</option>
              <option value="Net 30 Vendor Program">Net 30 Vendor Program</option>
            </select>
          </div>
        </div>

        {/* Supporting Documents */}
        <div className="mb-10">
          <h3 className="text-xl font-semibold text-[#2F6FE4] mb-6 pb-3 border-b-2 border-gray-100">
            Supporting Documents
          </h3>
          <div className="relative">
            <input
              type="file"
              id="fileUpload"
              name="fileUpload"
              multiple
              onChange={handleFileChange}
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
            />
            <label
              htmlFor="fileUpload"
              className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#2F6FE4] transition-colors"
            >
              <div className="text-center">
                <i className="ri-upload-cloud-line text-3xl text-gray-400 mb-2"></i>
                {fileNames.length > 0 ? (
                  <div className="space-y-1">
                    {fileNames.map((name, i) => (
                      <p key={i} className="text-sm text-[#2F6FE4] font-medium">{name}</p>
                    ))}
                    <p className="text-xs text-gray-400 mt-1">Click to change files</p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 font-medium">Click to upload documents (optional)</p>
                    <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG, DOC, XLS — multiple files supported</p>
                  </>
                )}
              </div>
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Upload documents such as business registration, EIN confirmation letter, or business credit reports if available.
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Uploaded files are securely stored and linked in the email notification sent to our team.
          </p>
        </div>

        {/* Additional Notes */}
        <div className="mb-10">
          <label htmlFor="additionalNotes" className="block text-sm font-medium text-gray-700 mb-2">
            Additional Notes
          </label>
          <textarea
            id="additionalNotes"
            name="additionalNotes"
            value={formData.additionalNotes}
            onChange={handleInputChange}
            maxLength={500}
            rows={5}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F6FE4] focus:border-transparent outline-none transition-all text-sm resize-none"
            placeholder="Add any additional information or special requests (max 500 characters)"
          />
          <p className="text-xs text-gray-500 mt-1">
            {formData.additionalNotes.length}/500 characters
          </p>
        </div>

        {/* Broker Confirmation */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-[#2F6FE4] mb-6 pb-3 border-b-2 border-gray-100">
            Broker Confirmation
          </h3>
          <div className="space-y-4">
            <label className="flex items-start cursor-pointer">
              <input type="checkbox" name="agreement1" checked={formData.agreement1} onChange={handleInputChange} required className="mt-1 w-5 h-5 text-[#2F6FE4] border-gray-300 rounded focus:ring-2 focus:ring-[#2F6FE4] cursor-pointer" />
              <span className="ml-3 text-sm text-gray-700">
                I confirm that the client listed above has agreed to the requested service and understands the associated pricing and service terms. <span className="text-red-500">*</span>
              </span>
            </label>
            <label className="flex items-start cursor-pointer">
              <input type="checkbox" name="agreement2" checked={formData.agreement2} onChange={handleInputChange} required className="mt-1 w-5 h-5 text-[#2F6FE4] border-gray-300 rounded focus:ring-2 focus:ring-[#2F6FE4] cursor-pointer" />
              <span className="ml-3 text-sm text-gray-700">
                I confirm that the information submitted on this form is accurate. <span className="text-red-500">*</span>
              </span>
            </label>
            <label className="flex items-start cursor-pointer">
              <input type="checkbox" name="agreement3" checked={formData.agreement3} onChange={handleInputChange} required className="mt-1 w-5 h-5 text-[#2F6FE4] border-gray-300 rounded focus:ring-2 focus:ring-[#2F6FE4] cursor-pointer" />
              <span className="ml-3 text-sm text-gray-700">
                I understand that services will not begin until payment has been received by The Broker Marketplace. <span className="text-red-500">*</span>
              </span>
            </label>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-[#2F6FE4] text-white font-semibold py-4 px-8 rounded-lg hover:bg-[#1e5bc6] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              Submitting...
            </span>
          ) : 'Submit Deal'}
        </button>

        {/* Status Messages */}
        {submitStatus === 'success' && (
          <div className="mt-6 p-6 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
              </div>
              <div>
                <p className="text-green-800 font-semibold mb-2">
                  Thank you. Your deal submission has been received.
                </p>
                <p className="text-green-700 text-sm">
                  Our team will review the information and contact the broker if additional details are needed.
                </p>
                <p className="text-green-700 text-sm mt-1">
                  Services will not begin until required payment has been received by The Broker Marketplace.
                </p>
              </div>
            </div>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm font-medium">
              <i className="ri-error-warning-line mr-2"></i>
              {errorMessage}
            </p>
          </div>
        )}
      </form>
    </div>
  );
};

export default DealSubmissionForm;
